import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import LanguageToggle from '../../components/LanguageToggle';
import { resolveEmployerId } from '../../utils/resolveEmployerId';
import { resolveUnionId, UNION_STAFF_ROLE } from '../../utils/resolveUnionId';
import VocationWorksLogo from '../../components/VocationWorksLogo';

const NAVY     = '#0D1B2A';
const NAVY_MID = '#132233';
const AMBER    = '#F5A623';
const WHITE    = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";
const RED       = '#DC2626';

// Whether this login may reactivate the deactivated account it signed in to. Workers only ever
// sign in to their own row. For employers and unions, invited staff act on the owner's account,
// so only the account's own login or an active owner seat counts -- the same rule
// trg_protect_employer_deactivation enforces for employers. Unions have no such database
// guard (their update policy just matches nothing for staff), so this check is what keeps a
// staff login from being offered a reactivation that can't happen.
const OWNER_STAFF_TABLE = { employers: ['employer_staff', 'employer_id'], union_accounts: ['union_staff', 'union_id'] };
async function canReactivate(table, accountId, userId) {
  if (accountId === userId) return true;
  const ownerSeat = OWNER_STAFF_TABLE[table];
  if (!ownerSeat) return false;
  const [staffTable, accountColumn] = ownerSeat;
  const { data } = await supabase.from(staffTable).select('role')
    .eq(accountColumn, accountId).eq('user_id', userId).eq('role', 'owner').eq('is_active', true)
    .maybeSingle();
  return !!data;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const accountType = new URLSearchParams(location.search).get('type') || 'worker';
  const isEmployer = accountType === 'employer';
  const isUnion    = accountType === 'union';
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [googleHover, setGoogleHover] = useState(false);
  // { table, userId, dashboardPath } for an owner who may reactivate, or { staffBlocked: true }
  // for a staff login whose company/union is deactivated (already signed out by then).
  const [reactivationPrompt, setReactivationPrompt] = useState(null);
  const [reactivating, setReactivating] = useState(false);
  const [reactivateError, setReactivateError] = useState(false);

  const handleGoogleLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin + '/auth/callback',
        queryParams: { prompt: 'select_account' },
      }
    })
    if (error) alert(error.message)
  }

  const handleEmailLogin = async () => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) { alert(error.message); return }

    const { data: { session } } = await supabase.auth.getSession()
    const userId = session?.user?.id

    let table, dashboardPath, accountId = userId
    // Not just the union signup's own metadata -- an invited union_staff seat has its own
    // auth.uid() and acts on the owner's union_id (resolveUnionId), same as employer staff.
    const metaRole = session?.user?.user_metadata?.role
    const unionId = (metaRole === 'union' || metaRole === UNION_STAFF_ROLE) ? await resolveUnionId(supabase, userId) : null
    if (unionId || metaRole === 'union' || metaRole === UNION_STAFF_ROLE) {
      // A deactivated staff seat resolves to no union -- still route them to the union
      // dashboard, which shows an explicit "access deactivated" state rather than the
      // worker flow. accountId only matters for the deactivated_at (whole-account) check.
      if (!unionId) { navigate('/union/dashboard'); return }
      table = 'union_accounts'
      dashboardPath = '/union/dashboard'
      accountId = unionId
    } else {
      // Not just "is this uid literally an employers row" -- an invited employer_staff
      // seat has its own auth.uid() but acts on the owner's employer_id.
      const employerId = await resolveEmployerId(supabase, userId)
      if (employerId) {
        table = 'employers'
        dashboardPath = '/employer/dashboard'
        accountId = employerId
      } else {
        // Organizations have no deactivated_at column (no reactivation flow), and
        // organization_staff's own "id" isn't the user id, so this role can't reuse
        // the generic accountRow lookup below -- route it directly instead.
        const { data: orgStaffRecord } = await supabase.from('organization_staff').select('id').eq('user_id', userId).maybeSingle()
        if (orgStaffRecord) {
          navigate('/organization/dashboard')
          return
        }
        table = 'workers'
        dashboardPath = '/worker/dashboard'
      }
    }

    const { data: accountRow } = await supabase.from(table).select('deactivated_at').eq('id', accountId).single()

    if (accountRow?.deactivated_at) {
      setReactivateError(false)
      if (await canReactivate(table, accountId, userId)) {
        setReactivationPrompt({ table, userId: accountId, dashboardPath })
      } else {
        // Signed out right away rather than on the button, so the session can't be used to
        // open the dashboard of an account they can't reactivate.
        await supabase.auth.signOut()
        setReactivationPrompt({ staffBlocked: true })
      }
      return
    }

    navigate(dashboardPath)
  }

  const handleReactivate = async () => {
    if (reactivating) return
    const { table, userId, dashboardPath } = reactivationPrompt
    setReactivating(true)
    setReactivateError(false)
    // Only counts as reactivated when the row comes back -- an update that RLS or a trigger
    // stops can come back empty rather than as an error, and must not open the dashboard.
    const { data: updated, error } = await supabase.from(table).update({ deactivated_at: null }).eq('id', userId).select('id')
    setReactivating(false)
    if (error || updated?.length !== 1) {
      setReactivateError(true)
      return
    }
    setReactivationPrompt(null)
    navigate(dashboardPath)
  }

  const handleDeclineReactivation = async () => {
    await supabase.auth.signOut()
    setReactivateError(false)
    setReactivationPrompt(null)
  }

  const handleForgotPassword = async () => {
    if (!email) {
      alert(t('auth.login.enterEmailFirst'))
      return
    }
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/reset-password'
    })
    if (error) {
      alert(error.message)
    } else {
      alert(t('auth.login.resetEmailSent'))
    }
  }

  useEffect(() => {
    const link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);

    const style = document.createElement('style');
    style.textContent = `
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { background-color: #F5F6F8; }
      .login-input::placeholder { color: rgba(0,0,0,0.3); }
      @media (max-width: 767px) {
        .login-content { align-items: flex-start !important; padding-left: 16px !important; padding-right: 16px !important; }
        .login-footer-note {
          position: static !important;
          transform: none !important;
          left: auto !important;
          bottom: auto !important;
          white-space: normal !important;
          font-size: 12px !important;
          text-align: center !important;
          margin-top: 20px !important;
          padding: 0 !important;
          width: auto !important;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(link);
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div style={{ fontFamily: FONT_BODY, minHeight: '100dvh', backgroundColor: '#F5F6F8', display: 'flex', flexDirection: 'column' }}>

      {/* Navbar */}
      <nav style={{
        height: '60px',
        backgroundColor: NAVY,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 2rem',
        flexShrink: 0,
      }}>
        <div
          onClick={() => navigate('/')}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
        >
          <VocationWorksLogo variant="reversed" size={36} />
        </div>
        <LanguageToggle />
      </nav>

      {/* Main content */}
      <div className="login-content" style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1.25rem',
      }}>
        <div style={{
          backgroundColor: WHITE,
          borderRadius: '16px',
          boxShadow: '0 4px 24px rgba(13,27,42,0.10)',
          padding: '40px',
          width: '100%',
          maxWidth: '400px',
        }}>

          {/* Title */}
          <h1 style={{
            fontFamily: FONT_BODY,
            fontSize: '24px',
            fontWeight: 700,
            color: NAVY,
            marginBottom: '6px',
          }}>
            {t('auth.login.welcomeBack')}
          </h1>
          <p style={{
            fontSize: '0.9rem',
            color: 'rgba(13,27,42,0.5)',
            marginBottom: '20px',
          }}>
            {t('auth.login.subtitle')}
          </p>

          {/* Account type tabs */}
          <div style={{
            display: 'flex', gap: '6px', marginBottom: '24px',
            backgroundColor: '#F0F1F4', borderRadius: '9px', padding: '4px',
          }}>
            {[
              { key: 'worker',   label: t('auth.login.tabWorker') },
              { key: 'employer', label: t('auth.login.tabEmployer') },
              { key: 'union',    label: t('auth.login.tabUnion') },
            ].map(opt => {
              const active = accountType === opt.key;
              return (
                <span
                  key={opt.key}
                  onClick={() => navigate(opt.key === 'worker' ? '/login' : `/login?type=${opt.key}`)}
                  style={{
                    flex: 1, textAlign: 'center', padding: '8px 6px',
                    borderRadius: '6px', cursor: 'pointer',
                    fontSize: '0.8125rem', fontWeight: 600,
                    backgroundColor: active ? WHITE : 'transparent',
                    color: active ? NAVY : 'rgba(13,27,42,0.5)',
                    boxShadow: active ? '0 1px 4px rgba(13,27,42,0.12)' : 'none',
                    transition: 'background-color 0.15s ease',
                  }}
                >
                  {opt.label}
                </span>
              );
            })}
          </div>

          {/* Google button */}
          <button
            onClick={handleGoogleLogin}
            onMouseEnter={() => setGoogleHover(true)}
            onMouseLeave={() => setGoogleHover(false)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              padding: '12px 16px',
              backgroundColor: googleHover ? '#F5F6F8' : WHITE,
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              cursor: 'pointer',
              fontFamily: FONT_BODY,
              fontSize: '0.9375rem',
              fontWeight: 500,
              color: '#1F2937',
              transition: 'background-color 0.15s ease',
              marginBottom: '24px',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 48 48">
              <path fill="#4285F4" d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z"/>
              <path fill="#34A853" d="M6.3 14.7l7 5.1C15 16.1 19.2 13 24 13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2c-7.6 0-14.2 4.1-17.7 10.2z" transform="translate(0,2)"/>
              <path fill="#FBBC05" d="M24 46c5.9 0 10.9-2 14.6-5.4l-6.8-5.6C29.8 36.6 27.1 37 24 37c-6.1 0-10.7-3.1-11.8-7.5l-7 5.4C8.2 41.9 15.5 46 24 46z" transform="translate(0,-2)"/>
              <path fill="#EA4335" d="M44.5 20H24v8.5h11.8c-1 3-3.2 5.4-6.2 6.9l6.8 5.6C40.3 37.4 44.5 31.2 44.5 24c0-1.3-.2-2.7-.5-4z"/>
            </svg>
            {t('auth.login.continueWithGoogle')}
          </button>

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: '#E5E7EB' }} />
            <span style={{ fontSize: '0.8125rem', color: 'rgba(13,27,42,0.4)', flexShrink: 0 }}>{t('auth.login.or')}</span>
            <div style={{ flex: 1, height: '1px', backgroundColor: '#E5E7EB' }} />
          </div>

          {/* Email + Password + Submit */}
          <form onSubmit={e => { e.preventDefault(); handleEmailLogin(); }}>

          {/* Email */}
          <div style={{ marginBottom: '18px' }}>
            <label style={{
              display: 'block',
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: NAVY,
              marginBottom: '6px',
            }}>
              {t('auth.login.emailLabel')}
            </label>
            <input
              className="login-input"
              type="email"
              placeholder={t('auth.login.emailPlaceholder')}
              value={email}
              onChange={e => setEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '11px 14px',
                fontSize: '0.9375rem',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                outline: 'none',
                fontFamily: FONT_BODY,
                color: NAVY,
                backgroundColor: WHITE,
              }}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: '28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
              <label style={{ fontSize: '0.8125rem', fontWeight: 600, color: NAVY }}>
                {t('auth.login.passwordLabel')}
              </label>
              <span
                onClick={handleForgotPassword}
                style={{ fontSize: '0.8125rem', color: AMBER, fontWeight: 500, cursor: 'pointer' }}
              >
                {t('auth.login.forgotPassword')}
              </span>
            </div>
            <input
              className="login-input"
              type="password"
              placeholder={t('auth.login.passwordPlaceholder')}
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '11px 14px',
                fontSize: '0.9375rem',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                outline: 'none',
                fontFamily: FONT_BODY,
                color: NAVY,
                backgroundColor: WHITE,
              }}
            />
          </div>

          {/* Sign in button */}
          <button
            type="submit"
            style={{
              width: '100%',
              padding: '13px',
              backgroundColor: AMBER,
              color: NAVY,
              border: 'none',
              borderRadius: '8px',
              fontFamily: FONT_BODY,
              fontSize: '0.9375rem',
              fontWeight: 700,
              cursor: 'pointer',
              marginBottom: '20px',
            }}
          >
            {t('auth.login.signInButton')}
          </button>

          </form>

          {/* Sign up link */}
          <p style={{ textAlign: 'center', fontSize: '0.875rem', color: 'rgba(13,27,42,0.55)' }}>
            {isEmployer ? t('auth.login.noAccountEmployer') : isUnion ? t('auth.login.noAccountUnion') : t('auth.login.noAccountWorker')}{' '}
            <span
              onClick={() => navigate(isEmployer ? '/employer/signup' : isUnion ? '/union/signup/step1' : '/worker/signup')}
              style={{ color: AMBER, fontWeight: 600, cursor: 'pointer' }}
            >
              {t('auth.login.signUpFree')}
            </span>
          </p>

          {/* Disclaimer — absolute on desktop (floats to viewport bottom), static inside card on mobile */}
          <p className="login-footer-note" style={{
            position: 'absolute',
            bottom: '1.5rem',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '0.75rem',
            color: 'rgba(13,27,42,0.4)',
            textAlign: 'center',
            width: '100%',
            padding: '0 1rem',
            whiteSpace: 'nowrap',
          }}>
            {t('auth.login.disclaimer')}
          </p>
        </div>
      </div>

      {reactivationPrompt && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          backgroundColor: 'rgba(0,0,0,0.65)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '16px',
        }}>
          <div style={{
            background: WHITE, borderRadius: '14px', padding: '28px',
            width: '100%', maxWidth: '420px', textAlign: 'center',
          }}>
            <h2 style={{ fontFamily: FONT_HEAD, fontSize: '1.4rem', letterSpacing: '0.03em', color: NAVY, marginBottom: '12px' }}>
              {t('auth.login.reactivateTitle')}
            </h2>
            <p style={{ fontFamily: FONT_BODY, fontSize: '0.9rem', color: 'rgba(13,27,42,0.65)', marginBottom: '22px', lineHeight: 1.5, overflowWrap: 'anywhere' }}>
              {reactivationPrompt.staffBlocked ? t('auth.login.staffDeactivatedBody') : t('auth.login.reactivateBody')}
            </p>
            {reactivateError && (
              <p
                role="alert"
                data-reactivate-error
                style={{
                  fontFamily: FONT_BODY, fontSize: '0.88rem', fontWeight: 600, color: RED, lineHeight: 1.5,
                  background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.3)',
                  borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', textAlign: 'left', overflowWrap: 'anywhere',
                }}
              >
                {t('auth.login.reactivateFailed')}
              </p>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {reactivationPrompt.staffBlocked ? (
                <button
                  onClick={() => setReactivationPrompt(null)}
                  style={{ background: AMBER, border: 'none', color: NAVY, fontFamily: FONT_BODY, fontWeight: 700, fontSize: '0.9rem', padding: '12px', borderRadius: '8px', cursor: 'pointer' }}
                >
                  {t('auth.login.backToSignIn')}
                </button>
              ) : (
                <>
                  <button
                    onClick={handleReactivate}
                    disabled={reactivating}
                    style={{ background: AMBER, border: 'none', color: NAVY, fontFamily: FONT_BODY, fontWeight: 700, fontSize: '0.9rem', padding: '12px', borderRadius: '8px', cursor: reactivating ? 'default' : 'pointer', opacity: reactivating ? 0.7 : 1 }}
                  >
                    {t('auth.login.reactivateButton')}
                  </button>
                  <button
                    onClick={handleDeclineReactivation}
                    disabled={reactivating}
                    style={{ background: 'transparent', border: '1px solid rgba(13,27,42,0.25)', color: 'rgba(13,27,42,0.7)', fontFamily: FONT_BODY, fontWeight: 600, fontSize: '0.9rem', padding: '12px', borderRadius: '8px', cursor: 'pointer' }}
                  >
                    {t('auth.login.reactivateDecline')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
